import { AdminApiError, type AdminClient } from "../api.js";
import {
  chartLoadIsGenuinelyUnloaded,
  mountChartChooser,
} from "../chart_chooser.js";
import {
  chartLoadFromError,
  chartLoadFromSnapshot,
  type ChartLoadState,
} from "../chart_load.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text";
import {
  literalRegexProbe,
  vendorScopeNameRegex,
} from "../../../ingest-worker/src/rule_overlap";
import {
  cardRuleNotAdded,
  sentence,
} from "../ui/sentences.js";
import { clear, h } from "../dom.js";
import { takeNavParams } from "../ui/nav-bus.js";
import {
  type CardRuleAsk,
  type CompanySlug,
  type LedgerChangeRequest,
  type LedgerChangeTarget,
  type Rule,
  type RuleChangeRequest,
  type StaffRole,
} from "../types.js";
import { card, disclosure, focusAfterWrite, pageHeader } from "../ui/components.js";
import { advice } from "../ui/capabilities.js";
import * as face from "../ui/face.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import {
  accountLabelBeside,
  dateText,
  moneyText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import { ruleAmountWords } from "../../../ingest-worker/src/rule_amount_spec";
import { readerRole } from "../ui/facts.js";
import { runWrite, type WriteOutcome } from "../ui/write.js";
import { isValidLocationCode } from "../../../packages/contracts/src/location-field-pattern";

export interface RulesPageOptions {
  client: AdminClient;
  operatorId: string;
  company: CompanySlug;
  root: HTMLElement;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /** Owner sees standing-rule CRUD; AM proposes CoA/mapping/vocabulary. */
  role?: StaffRole;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1): a rule's post account and a
   * between-companies change request name the company, never its code.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
  /**
   * rd15 A2: a rule a transaction's detail named, so the page opens on it.
   * Falls back to the navigation parameter when the shell did not pass one.
   */
  highlightRuleId?: number;
}

function resolvedRole(explicit: StaffRole | undefined): StaffRole {
  // Owner decision 10: an unknown role is the account manager (least access).
  return readerRole(explicit);
}

/** The page's own sentence under the title, the same on every state. */
function rulesLede(role: StaffRole): string {
  return role === "owner"
    ? "Standing rules say how a bank line is booked. Turn it off to stop using it. It stays in the list."
    : `A rule you make from a card starts on the next run. ${advice("change_rule", role)}`;
}

/**
 * The one sentence for a card ask that is not in the rules list. The reason
 * code maps to a decided sentence in `ui/sentences.ts`; a reason the page does
 * not know (a newer pipeline value) reads as the general sentence rather than
 * printing a code or "undefined" at a person (rd r13 L6 review 1).
 */
function cardAskStateWords(ask: CardRuleAsk, role: StaffRole): string {
  if (ask.state === "waiting") {
    return sentence("card_rule_waiting", role);
  }
  return cardRuleNotAdded(ask.reason, role);
}

/**
 * The load-error state, on its own. A failed first read lands here, and so does
 * a failed *post-write* re-read: a committed write must never be followed by a
 * thrown read, or the page stays on "Loading rules…" and the outcome is never
 * said (round-3 review, item 2).
 */
function renderRulesLoadError(opts: RulesPageOptions, role: StaffRole, error?: unknown): void {
  clear(opts.root);
  const page = h("div", { class: "settings-stack" });
  opts.root.append(page);
  const retry = h("button", {
    type: "button",
    class: "btn btn-primary",
    "data-action": "retry-rules",
    "aria-label": "Try again",
  }, ["Try again"]);
  retry.addEventListener("click", () => {
    // A retry whose read still fails must land back here, never on a page left
    // reading "Loading rules…" (round-3 review, item 1).
    void renderRulesPage(opts).catch((retryError: unknown) => {
      if (opts.isCurrent?.() ?? true) renderRulesLoadError(opts, role, retryError);
    });
  });
  const signedOut = error instanceof AdminApiError && error.status === 401;
  const copy = failureCopy(error, { role, context: "read" });
  page.append(
    pageHeader("Rules", rulesLede(role)),
    h("div", { class: "error-state", role: "alert", "data-role": "rules-load-error" }, [
      h("p", {}, [
        error === undefined ? "Couldn't load rules and proposals. Try again." : copy.sentence,
      ]),
      ...(copy.detail === null ? [] : [disclosure("Show details", h("p", {}, [copy.detail]))]),
      ...(signedOut ? [] : [retry]),
    ]),
  );
}

/** List company rules; append/deactivate enqueues a rule_change_request (202). */
export async function renderRulesPage(opts: RulesPageOptions): Promise<void> {
  const { client, operatorId, company, root } = opts;
  // A detail drawer's "Open rule 14" arrives here. Read once, so the page is
  // the only place that knows which rule the person came for.
  const navRule = takeNavParams("rules")?.rule;
  const highlightRuleId = opts.highlightRuleId ?? navRule;
  const role: StaffRole = resolvedRole(opts.role);
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);
  page.append(h("p", { class: "muted" }, ["Loading rules…"]));

  let rules: Rule[];
  /**
   * Rule requests that are waiting to be applied. A rule added through the
   * form below is not in `listRules` until the pipeline applies it, so the page
   * would say "No rules yet" right after the person made one (rd r13 L3.1).
   * Reading the pending rows also says a rule already has a turn-off waiting,
   * so a second press cannot file a duplicate.
   */
  let ruleRequests: RuleChangeRequest[];
  /**
   * Card answers whose rule is not in the list: waiting on the next run, or
   * not added and why. The Worker reads them with the rules, so the page has
   * one read for the whole list (rd r13 L6).
   */
  let cardAsks: CardRuleAsk[];
  let chartLoad: ChartLoadState;
  let coaInbox: Awaited<ReturnType<AdminClient["listLedgerChangeRequests"]>>;
  let mappingInbox: Awaited<ReturnType<AdminClient["listLedgerChangeRequests"]>>;
  let vocabInbox: Awaited<ReturnType<AdminClient["listLedgerChangeRequests"]>>;
  let coaPending: Awaited<ReturnType<AdminClient["listLedgerChangeRequests"]>>;
  let mappingPending: Awaited<ReturnType<AdminClient["listLedgerChangeRequests"]>>;
  let vocabPending: Awaited<ReturnType<AdminClient["listLedgerChangeRequests"]>>;
  try {
    // Both roles read the standing rules (rd r13 L3.1). The Worker allows an
    // account manager to read them; only the owner may add or turn one off.
    const data = await client.listRules(company);
    if (!(opts.isCurrent?.() ?? true)) return;
    rules = data.rules;
    // Tolerate an absent list: one missing array must not blank the rule list
    // (rd r13 L6 review 1 — a Worker mid-deploy still answers the older body).
    cardAsks = data.cardAsks ?? [];
    if (!(opts.isCurrent?.() ?? true)) return;
    try {
      const pending = await client.listRuleChangeRequests(company, { status: "pending" });
      if (!(opts.isCurrent?.() ?? true)) return;
      ruleRequests = pending.requests;
    } catch {
      // The rules themselves loaded. A failed requests read must not blank the
      // list; without it no rule is marked waiting, and the write path still
      // refuses a duplicate through the Worker's own 409.
      ruleRequests = [];
    }
    if (!(opts.isCurrent?.() ?? true)) return;

    try {
      chartLoad = chartLoadFromSnapshot(await client.getChartSnapshot(company));
    } catch {
      chartLoad = chartLoadFromError(company);
    }
    if (!(opts.isCurrent?.() ?? true)) return;

    [coaInbox, mappingInbox, vocabInbox, coaPending, mappingPending, vocabPending] =
      await Promise.all([
        client.listLedgerChangeRequests(company, "coa_account"),
        client.listLedgerChangeRequests(company, "account_mapping"),
        client.listLedgerChangeRequests(company, "vocabulary"),
        client.listLedgerChangeRequests(company, "coa_account", { status: "pending" }),
        client.listLedgerChangeRequests(company, "account_mapping", { status: "pending" }),
        client.listLedgerChangeRequests(company, "vocabulary", { status: "pending" }),
      ]);
  } catch (error) {
    if (!(opts.isCurrent?.() ?? true)) return;
    if (
      error instanceof AdminApiError &&
      (error.status === 401 || error.status === 403 || error.status === 503)
    ) {
      throw error;
    }
    renderRulesLoadError(opts, role);
    return;
  }
  if (!(opts.isCurrent?.() ?? true)) return;
  const snapshot = chartLoad.snapshot;

  clear(page);
  page.append(pageHeader("Rules", rulesLede(role)));

  const banner = h("p", { class: "banner", role: "status", "data-role": "rule-banner", hidden: "true" }, []);
  page.append(banner);

  /**
   * Re-read the page after a write so the lists show what was stored, then put
   * the outcome sentence on the fresh banner. Rebuilding drops the old banner,
   * so the sentence has to be said again on the new one (R7-15).
   */
  const announceAfterWrite = async (
    message: string,
    ok: boolean,
    detail?: string,
  ): Promise<void> => {
    try {
      await renderRulesPage(opts);
    } catch (rereadError: unknown) {
      // The write committed and only the re-read failed. A rethrow here would
      // leave the page on "Loading rules…" with the outcome unsaid — for a 401,
      // 403 or 503 the same as for any other status (round-3 review, item 2).
      if (!(opts.isCurrent?.() ?? true)) return;
      renderRulesLoadError(opts, role, rereadError);
    }
    if (!(opts.isCurrent?.() ?? true)) return;
    const nextBanner = root.querySelector<HTMLElement>('[data-role="rule-banner"]');
    if (nextBanner !== null) {
      showBanner(nextBanner, message, ok, detail);
      focusAfterWrite(nextBanner);
      return;
    }
    // The write committed but the re-read failed: the fresh page is the
    // load-error state and has no banner, so the sentence would vanish and the
    // person would read a failure for a request that was stored (round-2
    // review, item 3). Put it above the error, not behind it.
    const loadError = root.querySelector<HTMLElement>('[data-role="rules-load-error"]');
    if (loadError !== null) {
      const bannerEl = h("p", {
        class: "banner",
        role: "status",
        "data-role": "rule-banner",
      });
      showBanner(bannerEl, message, ok, detail);
      loadError.parentElement?.insertBefore(bannerEl, loadError);
      focusAfterWrite(bannerEl);
    }
  };

  const rulesCard = card();
  page.append(rulesCard);
  renderRulesSection({
    client,
    operatorId,
    company,
    companies,
    root: rulesCard,
    rules,
    ruleRequests,
    chartLoad,
    snapshot,
    banner,
    rerender: announceAfterWrite,
    stillCurrent: () => opts.isCurrent?.() ?? true,
    role,
    ...(highlightRuleId === undefined ? {} : { highlightRuleId }),
  });

  // Card answers whose rule is not in the list yet, or was not added and why.
  // The rule itself, once the run makes it, is a row in the list above, with
  // "Turn off" like every other rule (rd r13 L6).
  if (cardAsks.length > 0) {
    const askedCard = card();
    page.append(askedCard);
    askedCard.append(
      h("h2", { "data-role": "card-rules-heading" }, ["Rules asked for on a card"]),
      h("p", { class: "muted", "data-role": "card-rules-copy" }, [
        "When you answer a card you can ask for a standing rule. It starts on the next update, and then it moves into the list above.",
      ]),
    );
    const askedList = h("ul", { class: "rules-list", "data-role": "card-rules" });
    for (const ask of cardAsks) {
      askedList.append(
        renderCardRuleAsk(ask, snapshot.selectableAccounts, companies, role),
      );
    }
    askedCard.append(askedList);
  }

  const awaitingCard = card();
  page.append(awaitingCard);
  awaitingCard.append(h("h2", { "data-role": "change-inbox-heading" }, ["Waiting for approval"]));
  const awaitingGroups: Array<{
    target: LedgerChangeTarget;
    label: string;
    requests: LedgerChangeRequest[];
  }> = [
    { target: "coa_account", label: "Chart of accounts", requests: coaInbox.requests },
    { target: "account_mapping", label: "Bank mappings", requests: mappingInbox.requests },
    { target: "vocabulary", label: "Places", requests: vocabInbox.requests },
  ];
  // With nothing waiting, nobody is asked to approve anything: the line below
  // the heading used to tell an account manager to ask the owner for an
  // approval while the list itself said "Nothing is waiting for approval."
  // (rd r13 L3.2). The sentence is only true when a row is there to act on.
  if (awaitingGroups.some((group) => group.requests.length > 0)) {
    awaitingCard.append(
      h("p", { class: "muted", "data-role": "change-inbox-copy" }, [
        // The owner's next step comes from the capability table, so the account
        // manager is told the owner's own control rather than a sentence typed
        // here (rd r13 review 1, item 4).
        "Account, bank mapping and place requests are no longer offered, so approving one would not apply it. " +
          advice("reject_change_request", role),
      ]),
    );
  }
  renderChangeInbox({
    client,
    company,
    companies,
    root: awaitingCard,
    banner,
    rerender: announceAfterWrite,
    stillCurrent: () => opts.isCurrent?.() ?? true,
    role,
    mode: "awaiting",
    offeredAccounts: snapshot.selectableAccounts,
    groups: awaitingGroups,
  });

  const pendingCard = card();
  page.append(pendingCard);
  pendingCard.append(
    h("h2", { "data-role": "pending-unapplied-heading" }, [
      "Approved changes that will not be applied",
    ]),
  );
  const pendingGroups: Array<{
    target: LedgerChangeTarget;
    label: string;
    requests: LedgerChangeRequest[];
  }> = [
    { target: "coa_account", label: "Chart of accounts", requests: coaPending.requests },
    { target: "account_mapping", label: "Bank mappings", requests: mappingPending.requests },
    { target: "vocabulary", label: "Places", requests: vocabPending.requests },
  ];
  if (pendingGroups.some((group) => group.requests.length > 0)) {
    pendingCard.append(
      h("p", { class: "muted", "data-role": "pending-unapplied-copy" }, [
        "Account, bank mapping and place changes are no longer offered, so these will not be applied. Nothing has been booked from them.",
      ]),
    );
  }
  renderChangeInbox({
    client,
    company,
    companies,
    root: pendingCard,
    banner,
    rerender: announceAfterWrite,
    stillCurrent: () => opts.isCurrent?.() ?? true,
    role,
    mode: "pending",
    offeredAccounts: snapshot.selectableAccounts,
    groups: pendingGroups,
  });
}

function renderRulesSection(args: {
  client: AdminClient;
  operatorId: string;
  company: CompanySlug;
  companies: AccountLabelContext;
  root: HTMLElement;
  rules: Rule[];
  ruleRequests: RuleChangeRequest[];
  chartLoad: ChartLoadState;
  snapshot: ChartLoadState["snapshot"];
  banner: HTMLElement;
  rerender: (message: string, ok: boolean, detail?: string) => Promise<void>;
  stillCurrent: () => boolean;
  role: StaffRole;
  /** rd15 A2: the rule a transaction's detail named, marked in the list. */
  highlightRuleId?: number;
}): void {
  const {
    highlightRuleId,
    client,
    operatorId,
    company,
    companies,
    root,
    rules,
    ruleRequests,
    chartLoad,
    snapshot,
    banner,
    rerender,
    stillCurrent,
    role,
  } = args;
  const waiting = pendingRuleState(ruleRequests);
  const list = h("ul", { class: "rules-list", "data-role": "rules-list" });
  for (const rule of rules) {
    list.append(
      renderRuleRow(
        rule,
        client,
        company,
        banner,
        snapshot.selectableAccounts,
        companies,
        rerender,
        stillCurrent,
        role,
        {
          waitingToApply: false,
          turnOffRequested: rule.turnOffRequested === true,
          fromCard: rule.source?.kind === "card",
        },
      ),
    );
  }
  // A rule that is waiting to be applied is a standing rule the person just
  // made: `listRules` only replays applied requests, so without this the page
  // said "No rules yet" straight after a successful add (rd r13 L3.1).
  for (const pending of waiting.appends) {
    list.append(
      renderRuleRow(
        pending.rule,
        client,
        company,
        banner,
        snapshot.selectableAccounts,
        companies,
        rerender,
        stillCurrent,
        role,
        { waitingToApply: true, turnOffRequested: false, requestId: pending.request.id },
      ),
    );
  }
  if (rules.length === 0 && waiting.appends.length === 0) {
    list.append(
      h("li", { class: "empty", "data-role": "rules-empty" }, [
        // This list is every standing rule the Worker can read: applied from
        // the form below, the owner's applied requests, and the rules the
        // nightly run makes from card answers (rd r13 L6). A card answer whose
        // rule is not made yet is listed in its own section below, so the
        // sentence never promises a rule that is not there.
        role === "owner"
          ? "No rules yet. A rule you add below appears here once the next update applies it."
          : "No rules yet. A rule the owner adds appears here once the next update applies it.",
      ]),
    );
  }
  root.append(list);

  // rd15 A2: the rule a transaction named is marked, and one sentence says why
  // the page opened on it. A rule the list does not hold says so; it never
  // silently opens the unmarked list.
  if (highlightRuleId !== undefined) {
    const row = list.querySelector(
      `[data-rule-id="${String(highlightRuleId)}"]`,
    );
    if (row instanceof HTMLElement) {
      row.classList.add("rule-highlight");
      row.setAttribute("data-rule-highlighted", "true");
      list.before(
        h("p", { class: "muted", "data-role": "rule-highlight-note", role: "status" }, [
          `This is the rule that booked the line you opened.`,
        ]),
      );
      if (typeof row.scrollIntoView === "function") {
        row.scrollIntoView({ block: "center" });
      }
    } else {
      root.append(
        h("p", { class: "muted", "data-role": "rule-highlight-missing", role: "status" }, [
          `The rule that booked that line is not in the list now.`,
        ]),
      );
    }
  }

  // The account manager reads the list; the add form and the turn-off control
  // are the owner's (capabilities: change_rule).
  if (role !== "owner") return;

  root.append(h("h2", {}, ["Add a rule"]));
  const matchInput = h("input", {
    type: "text",
    class: "match-input",
    placeholder: "For example, SYNTHETIC VENDOR",
    "aria-label": "Vendor name",
  });
  const accountChooser = mountChartChooser({
    kind: "account",
    selectable: snapshot.selectableAccounts,
    companies,
    className: "account-input",
    ariaLabel: "Post account",
    placeholder: "For example, Repairs",
    load: chartLoad,
  });
  const locationChooser = mountChartChooser({
    kind: "location",
    selectable: snapshot.selectableLocations,
    companies,
    className: "location-input",
    ariaLabel: "Post location",
    placeholder: "Place",
    load: chartLoad,
  });
  locationChooser.filter.setAttribute("data-role", "rule-location-input");
  const appendBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "aria-label": "Add rule",
    },
    ["Add a rule"],
  );

  appendBtn.addEventListener("click", () => {
    void (async () => {
      const merchant = matchInput.value.trim();
      const account = accountChooser.getValue();
      const location = locationChooser.getValue();
      if (!merchant || !account) {
        showBanner(
          banner,
          !merchant || accountChooser.filter.value.trim().length === 0
            ? "Enter a vendor and choose an account from the list."
            : "Choose an account from the list.",
          false,
        );
        return;
      }
      if (!location) {
        showBanner(
          banner,
          locationChooser.filter.value.trim().length === 0
            ? "Location is required. Choose one from the list."
            : "Choose a place from the list.",
          false,
        );
        return;
      }
      // The rule's location is written to the books, so it keeps the shared
      // writer-safe check (fail closed) even though the place form is gone.
      // Reaching here means the chooser handed back a stored name (an
      // unlisted typed word is caught above with null), so "choose a place
      // from the list" would be false: the place did come from the list and
      // is the books' own text that cannot be written (round-2 review, item
      // 13).
      if (!isValidLocationCode(location)) {
        showBanner(banner, "That place cannot be written to the books. Choose another.", false);
        return;
      }
      appendBtn.disabled = true;
      const rule: Rule = {
        id: 0,
        // The field is a vendor name, not a hand-written regular expression:
        // escape it, prefix the case-insensitive flag, and bound the match
        // the same way a queue-minted rule does (R7-02). A raw regex here made
        // "Shell" book "Shellfish Co." and made "Store (North" a 400.
        match: { merchant_regex: vendorScopeNameRegex(merchant) },
        post: { account, location },
        createdBy: operatorId,
        createdAt: new Date().toISOString().slice(0, 10),
        active: true,
      };
      // One press, one request: the write gate disables the whole form row
      // while it runs (rd round 12, A5-1).
      const outcome = await runWrite({
        key: "rule-append",
        scope: appendBtn.parentElement ?? root,
        gate: () => null,
        send: () => client.requestRuleChange(company, { action: "append", rule }),
        afterLanded: (queued) =>
          rerender(
            ruleWriteSentence("append", queued.status),
            true,
            `Request ${queued.id}, status ${queued.status}.`,
          ),
        stillCurrent,
      });
      if (outcome.kind !== "landed") {
        // An append's 409 falls through to the shared write sentence, like any
        // other failed write; the server's words stay in Show details.
        const copy = writeFailureCopy(outcome, "owner");
        showBanner(banner, copy.sentence, false, copy.detail ?? undefined);
        appendBtn.disabled = false;
      }
    })();
  });

  root.append(
    h("div", { class: "form-row" }, [
      h("label", {}, ["Vendor", matchInput]),
      h("label", {}, ["Account", accountChooser.root]),
      h("label", {}, ["Location", locationChooser.root]),
      appendBtn,
    ]),
  );
  const matchCopy = h(
    "p",
    { class: "muted", "data-role": "rule-match-copy" },
    [ruleMatchCopy("")],
  );
  matchInput.addEventListener("input", () => {
    matchCopy.textContent = ruleMatchCopy(matchInput.value.trim());
  });
  root.append(matchCopy);
  if (chartLoad.status === "error") {
    root.append(
      h("p", { class: "muted", "data-role": "chart-snapshot-error" }, [
        "The account list did not load. Try again from the top of the page.",
        // The stored message ends "...escalate to the owner", and this form is the
        // owner's: the owner is never sent to the owner (rd polish rules §1).
        disclosure("Show details", h("p", {}, ["The chart could not be loaded."])),
      ]),
    );
  } else if (chartLoadIsGenuinelyUnloaded(chartLoad)) {
    root.append(
      h("p", { class: "muted", "data-role": "chart-snapshot-absent" }, [
        `The account list is not loaded yet. ${advice("open_books", role)} Then return here to choose an account.`,
      ]),
    );
  }
  // The chart card that used to sit here opened, closed, mapped and placed
  // accounts, and nothing in Books applies those requests. The owner's words
  // (2026-10-30): "No, we don't." The forms are gone; existing requests are
  // listed read-only below (R7-14).
}

function renderChangeInbox(args: {
  client: AdminClient;
  company: CompanySlug;
  companies: AccountLabelContext;
  root: HTMLElement;
  banner: HTMLElement;
  role: StaffRole;
  mode: "awaiting" | "pending";
  /** Re-read the page after a write, then say what happened on the fresh banner. */
  rerender: (message: string, ok: boolean, detail?: string) => Promise<void>;
  stillCurrent: () => boolean;
  /** The accounts the company offers: the words the approver reads name one apart from its twin. */
  offeredAccounts: readonly string[];
  groups: Array<{
    target: LedgerChangeTarget;
    label: string;
    requests: LedgerChangeRequest[];
  }>;
}): void {
  const list = h("ul", {
    class: "change-inbox",
    "data-role": args.mode === "pending" ? "pending-unapplied" : "change-inbox",
  });
  let any = false;
  for (const group of args.groups) {
    for (const row of group.requests) {
      any = true;
      list.append(renderInboxRow(row, group.target, group.label, args));
    }
  }
  if (!any) {
    list.append(
      h("li", {
        class: "empty",
        "data-role":
          args.mode === "pending" ? "pending-unapplied-empty" : "change-inbox-empty",
      }, [
        // With nothing waiting there is nothing to approve, so neither role is
        // asked to approve anything (rd r12 F1, R8).
        args.mode === "pending"
          ? "No approved account, bank mapping or place changes are here."
          : "Nothing is waiting for approval.",
      ]),
    );
  }
  args.root.append(list);
}

function renderInboxRow(
  row: LedgerChangeRequest,
  target: LedgerChangeTarget,
  label: string,
  args: {
    client: AdminClient;
    company: CompanySlug;
    companies: AccountLabelContext;
    banner: HTMLElement;
    role: StaffRole;
    mode: "awaiting" | "pending";
    rerender: (message: string, ok: boolean, detail?: string) => Promise<void>;
    stillCurrent: () => boolean;
    offeredAccounts: readonly string[];
  },
): HTMLLIElement {
  const item = h("li", {
    class: "change-inbox-row",
    "data-role": "change-inbox-row",
    "data-change-id": row.id,
    "data-target": target,
  }, [
    h("div", {}, [
      h("strong", {}, [`${label} · ${actionLabel(row.action)} · ${statusLabel(row.status)}`]),
      // What is being approved, in words. Without it the person approves "Open
      // an account" without seeing which account (integration review E7).
      h("p", { "data-role": "change-inbox-subject" }, [
        ledgerChangeSubject(row, args.offeredAccounts, args.companies),
      ]),
      // These three request types have no applier (owner, 2026-10-30: "No, we
      // don't."), so an approved row says plainly that it will not be applied
      // (R7-14).
      h("p", { "data-role": "change-inbox-not-applied" }, ["This will not be applied."]),
      disclosure(
        "Show details",
        h("p", {}, [
          amVisibleDisplayText(`${row.id} · ${row.status} · ${row.payloadJson ?? ""}`),
        ]),
      ),
    ]),
  ]);
  if (args.role === "owner" && args.mode === "awaiting") {
    // No Approve: nothing applies these. Reject stays so the list can be
    // cleared.
    const reject = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-role": "reject-ledger-change",
        // The capability table names this control by its hook, so the button
        // carries the same key its row does (rd r13 review 1, item 4).
        "data-action": "reject-ledger-change",
        "aria-label": `Reject ${row.action} ${row.id}`,
      },
      ["Reject"],
    );
    reject.addEventListener("click", () => {
      void (async () => {
        reject.disabled = true;
        const outcome = await runWrite({
          key: `rule-reject:${row.id}`,
          scope: item,
          gate: () => null,
          send: () =>
            args.client.decideLedgerChangeRequest(args.company, target, row.id, {
              decision: "reject",
            }),
          afterLanded: (decided) =>
            // Re-read so the rejected row leaves the list (round-2 review, item
            // 5): saying "Rejected" over a row that is still there reads as if
            // nothing was stored.
            args.rerender(
              "Rejected. It will not be applied.",
              true,
              `${decided.id} · ${decided.status}`,
            ),
          stillCurrent: args.stillCurrent,
        });
        if (outcome.kind === "landed") return;
        const copy = writeFailureCopy(outcome, args.role);
        if (
          outcome.error instanceof AdminApiError &&
          (outcome.error.status === 404 || outcome.error.status === 409)
        ) {
          // Somebody else already decided it: a repeat can never succeed, so
          // say the shared sentence and re-read instead of inviting a retry.
          await args.rerender(copy.sentence, true, copy.detail ?? undefined);
          return;
        }
        showBanner(args.banner, copy.sentence, false, copy.detail ?? undefined);
        reject.disabled = false;
      })();
    });
    item.append(reject);
  }
  return item;
}

interface RuleRowState {
  /** A rule request that is stored but not applied yet. */
  waitingToApply: boolean;
  /** A turn-off request for this rule is stored and not applied yet. */
  turnOffRequested: boolean;
  /** The request id, for a row that has no applied rule id yet. */
  requestId?: string;
  /** The rule was made from a card answer, so the row says so. */
  fromCard?: boolean;
}

interface PendingRuleState {
  appends: Array<{ request: RuleChangeRequest; rule: Rule }>;
}

/**
 * The pending rule requests, read from their own payloads. Only the fields the
 * row shows are trusted: an unreadable payload is skipped rather than printed.
 */
function pendingRuleState(requests: readonly RuleChangeRequest[]): PendingRuleState {
  const appends: PendingRuleState["appends"] = [];
  for (const request of requests) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(request.payloadJson ?? "");
    } catch {
      continue;
    }
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) continue;
    const payload = parsed as Record<string, unknown>;
    // A queued turn-off is not an append; the server marks the rule it
    // targets with `turnOffRequested`, so nothing is added to the list here.
    if (request.action === "deactivate") continue;
    const raw = payload.rule;
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) continue;
    const rule = raw as Record<string, unknown>;
    const post = rule.post;
    const match = rule.match;
    if (post === null || typeof post !== "object" || Array.isArray(post)) continue;
    if (match === null || typeof match !== "object" || Array.isArray(match)) continue;
    const postFields = post as Record<string, unknown>;
    const account = postFields.account;
    const location = postFields.location;
    if (typeof account !== "string" || typeof location !== "string") continue;
    // Both objects are untrusted JSON. Read only the string fields the row
    // knows how to show; never hand an arbitrary shape to the formatters.
    const matchFields = match as Record<string, unknown>;
    const cleanMatch: Rule["match"] = {};
    for (const key of [
      "name_regex",
      "merchant_regex",
      "account_last4",
      "amount",
    ] as const) {
      const value = matchFields[key];
      if (typeof value === "string") cleanMatch[key] = value;
    }
    appends.push({
      request,
      rule: {
        id: typeof rule.id === "number" ? rule.id : 0,
        match: cleanMatch,
        post: { account, location },
        createdBy: typeof rule.createdBy === "string" ? rule.createdBy : "",
        createdAt: typeof rule.createdAt === "string" ? rule.createdAt : "",
        active: true,
      },
    });
  }
  return { appends };
}

/**
 * One card answer that asked for a standing rule and is not in the rules list
 * yet, in the words the card used. It states only what the data supports —
 * the payee, where the answer booked it, when it was answered, and whether it
 * is waiting or was not added and why — and offers no control: there is no
 * rule to turn off until the run makes one (rd r13 L6).
 */
function renderCardRuleAsk(
  ask: CardRuleAsk,
  offeredAccounts: readonly string[],
  companies: AccountLabelContext,
  role: StaffRole,
): HTMLLIElement {
  const when = dateText(ask.answeredAt);
  const place = ask.location === "" ? "" : ` at ${amVisibleDisplayText(ask.location)}`;
  const account =
    ask.account === ""
      ? ""
      : accountLabelBeside(ask.account, offeredAccounts, companies);
  return h(
    "li",
    {
      class: ask.state === "not_added" ? "rule-row inactive" : "rule-row pending",
      "data-role": "card-rule-row",
      "data-card-rule-id": String(ask.cardId),
      "data-card-rule-state": ask.state,
    },
    [
      h("div", { class: "rule-main" }, [
        h("strong", {}, [
          amVisibleDisplayText(ask.payee.trim() === "" ? "A card answer" : ask.payee),
        ]),
        h("span", { class: "muted" }, [
          `${account === "" ? "" : ` ${account}`}${place}${when === "" ? "" : ` · answered ${when}`}`,
        ]),
        h("p", { class: "muted", "data-role": "card-rule-outcome" }, [
          cardAskStateWords(ask, role),
        ]),
      ]),
    ],
  );
}

function renderRuleRow(
  rule: Rule,
  client: AdminClient,
  company: CompanySlug,
  banner: HTMLElement,
  offeredAccounts: readonly string[],
  companies: AccountLabelContext,
  rerender: (message: string, ok: boolean, detail?: string) => Promise<void>,
  stillCurrent: () => boolean,
  role: StaffRole,
  state: RuleRowState,
): HTMLLIElement {
  // The stored match is a regular expression (queue-minted or escaped by this
  // page); a person reads the words it matches, not the pattern. A pattern
  // that is not a literal stays unnamed rather than printing metacharacters on
  // the face (round-2 review, item 7).
  const merchantPattern = rule.match.merchant_regex
    ? (literalRegexProbe(rule.match.merchant_regex) ?? "a custom pattern")
    : null;
  // Queue-minted rules match the vendor name, not the merchant: `name_regex`
  // carries the same escaped literal, so it gets the same treatment rather
  // than printing the pattern on the face (round-3 review, item 1).
  const namePattern = rule.match.name_regex
    ? (literalRegexProbe(rule.match.name_regex) ?? "a custom pattern")
    : null;
  const matchBits = [
    merchantPattern ? amVisibleDisplayText(merchantPattern) : null,
    namePattern ? amVisibleDisplayText(namePattern) : null,
    rule.match.account_last4 ? `ending ${amVisibleDisplayText(rule.match.account_last4)}` : null,
    // R12 A2-3: the sign on a rule pin is a direction, not a negative amount.
    // The codec says it in words; the raw spec never reaches the face.
    rule.match.amount ? ruleAmountWords(rule.match.amount, moneyText) : null,
  ].filter((v): v is string => v !== null);

  // The server says what the data supports (rd r13 L6): `on` only when an
  // active rule is in rules.yaml, `off` when it is listed and no longer
  // applies. A rule with no state (a pending append read from its request)
  // falls back to `active`, which is true until the run applies it.
  const isOn = rule.state === undefined ? rule.active : rule.state === "on";
  const statusWord = state.waitingToApply
    ? "Waiting to be applied"
    : state.turnOffRequested
      ? "Turn-off requested"
      : isOn
        ? "On"
        : "Off";
  const row = h(
    "li",
    state.requestId === undefined
      ? {
          class: state.turnOffRequested || !isOn ? "rule-row inactive" : "rule-row",
          "data-rule-id": String(rule.id),
        }
      : {
          class: "rule-row pending",
          "data-rule-request-id": state.requestId,
        },
    [
      h("div", { class: "rule-main" }, [
        // The account names its company when it is scoped to one (rd13 L1).
        h("strong", {}, [
          accountLabelBeside(rule.post.account, offeredAccounts, companies),
        ]),
        h("span", { class: "muted" }, [
          ` ${amVisibleDisplayText(rule.post.location)} · ${matchBits.join(", ") || "No vendor match yet"} · ${statusWord}`,
        ]),
        // A rule the nightly run made from a card answer says so: the owner
        // asked to see them on this page, and a person reading the list knows
        // which rules they set from a card (rd r13 L6).
        ...(state.fromCard === true
          ? [
              h("span", { class: "muted", "data-role": "rule-source" }, [
                " From a card answer.",
              ]),
            ]
          : []),
        disclosure(
          "Show details",
          h("p", {}, [
            state.requestId === undefined
              ? `${rule.post.account} · Rule ${String(rule.id)}`
              : rule.post.account,
          ]),
        ),
      ]),
    ],
  );

  if (state.waitingToApply) {
    row.append(
      h("p", { class: "muted", "data-role": "rule-waiting" }, [
        sentence("card_rule_waiting", role),
      ]),
    );
  } else if (state.turnOffRequested) {
    row.append(
      h("p", { class: "muted", "data-role": "rule-turn-off-waiting" }, [
        "Turn-off requested. It applies on the next update.",
      ]),
    );
  }

  if (role === "owner" && isOn && !state.waitingToApply && !state.turnOffRequested) {
    const deactivate = h("button", {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "deactivate",
      "aria-label": `Turn off rule ${String(rule.id)}`,
    }, ["Turn off"]);
    deactivate.addEventListener("click", () => {
      void (async () => {
        deactivate.disabled = true;
        const outcome = await runWrite({
          key: `rule-deactivate:${String(rule.id)}`,
          scope: row,
          gate: () => null,
          send: () => client.requestRuleChange(company, { action: "deactivate", ruleId: rule.id }),
          // Each action says what it did, and the stored status decides
          // whether it is done: pending is only asked for (R7-15).
          afterLanded: (queued) =>
            rerender(
              ruleWriteSentence("deactivate", queued.status),
              true,
              `Request ${queued.id}, status ${queued.status}.`,
            ),
          stillCurrent,
        });
        if (outcome.kind === "landed") return;
        const copy = writeFailureCopy(outcome, role);
        if (outcome.error instanceof AdminApiError && outcome.error.status === 400) {
          // A rule that is already off can never be turned off again: this is a
          // retry that cannot succeed, so it reads as the decided "already done"
          // outcome (rd r11 §4), in the failure style rather than a green
          // banner, and the list is re-read so the stale row leaves it
          // (round-2 review M3).
          await rerender(outcomeSentence({ kind: "refused", status: 404 }, role), false);
          return;
        }
        if (outcome.error instanceof AdminApiError && outcome.error.status === 409) {
          // Already asked for: a repeat can never succeed, so the shared
          // sentence is said and the list re-read.
          await rerender(copy.sentence, true, copy.detail ?? undefined);
          return;
        }
        showBanner(banner, copy.sentence, false, copy.detail ?? undefined);
        deactivate.disabled = false;
        // The button is now disabled and cannot keep focus: move it to the
        // result, which is what a screen reader should read next.
        focusAfterWrite(banner);
      })();
    });
    row.append(deactivate);
  }
  return row;
}

/** What the request's payload names, as one plain sentence. */
const CHARACTER_WORDS: Readonly<Record<string, string>> = {
  loan: "a loan account",
  intercompany: "for money between companies",
  equity: "an equity account",
};

function payloadText(payload: Record<string, unknown>, key: string): string {
  const value = payload[key];
  return typeof value === "string" ? amVisibleDisplayText(value).trim() : "";
}

/**
 * The subject of a chart-of-accounts, bank-mapping or place request, read only
 * from the fields its payload already carries: the account (in words, never a
 * colon path, and told apart from a twin that reads the same, so the person
 * approves the account the request names), the last four digits of a bank account, or the place name. A
 * payload that cannot be read says so; it never falls back to showing JSON on
 * the face.
 */
export function ledgerChangeSubject(
  row: LedgerChangeRequest,
  offeredAccounts: readonly string[],
  companies: AccountLabelContext,
): string {
  const unreadable = "This request has no readable details.";
  let parsed: unknown;
  try {
    parsed = JSON.parse(row.payloadJson ?? "");
  } catch {
    return unreadable;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return unreadable;
  }
  const payload = parsed as Record<string, unknown>;
  const account = payloadText(payload, "account");
  if (row.action === "open" && account !== "") {
    const character = CHARACTER_WORDS[payloadText(payload, "character")];
    return `Open the account ${accountLabelBeside(account, offeredAccounts, companies)}${character === undefined ? "" : ` (${character})`}.`;
  }
  if (row.action === "close" && account !== "") {
    return `Close the account ${accountLabelBeside(account, offeredAccounts, companies)}.`;
  }
  if (row.action === "map" && account !== "") {
    const mask = payloadText(payload, "mask");
    return /^\d{4}$/.test(mask)
      ? `Link the bank account ending ${mask} to ${accountLabelBeside(account, offeredAccounts, companies)}.`
      : `Link a bank account to ${accountLabelBeside(account, offeredAccounts, companies)}.`;
  }
  const term = payloadText(payload, "term");
  if (row.action === "add_term" && term !== "") {
    return `Add the place “${term}”.`;
  }
  return unreadable;
}

function actionLabel(action: string): string {
  if (action === "open") return "Open an account";
  if (action === "close") return "Close an account";
  if (action === "map") return "Map a bank account";
  if (action === "add_term") return "Add a place";
  if (action === "append") return "Add a rule";
  if (action === "deactivate") return "Turn a rule off";
  return "Change";
}

function statusLabel(status: string): string {
  if (status === "awaiting_approval") return "Waiting for approval";
  // "Approved": the applier for these three types does not exist, so this must
  // not say "waiting to be applied" (R7-14).
  if (status === "pending") return "Approved";
  if (status === "rejected") return "Rejected";
  if (status === "applied") return "Applied";
  return "Recorded";
}

/**
 * What a rule write says the person did, from the status the server returned.
 * A pending request is not "Added": it is asked for, and applies on the next
 * update (R7-15). A final status is spoken as the fact it is.
 */
function ruleWriteSentence(
  action: "append" | "deactivate",
  status: string,
): string {
  if (status === "applied") {
    return action === "append" ? "Added. It is in the list now." : "Turned off. It is off now.";
  }
  if (status === "rejected") return "Rejected. It will not be applied.";
  if (status === "failed") {
    return action === "append"
      ? "Asked to add, but it did not apply. Try again."
      : "Asked to turn off, but it did not apply. Try again.";
  }
  return action === "append"
    ? "Asked to add. It applies on the next update."
    : "Asked to turn off. It applies on the next update.";
}

/** The one sentence saying what the rule will match, in the person's words. */
function ruleMatchCopy(merchant: string): string {
  const shown = amVisibleDisplayText(merchant).trim();
  return shown.length === 0
    ? "Every payment whose payee matches the text you type."
    : `Every payment whose payee matches ${shown}.`;
}

/** The one way a rules write's outcome becomes words (dispatch-b §3.1). */
function writeFailureCopy(
  outcome: WriteOutcome,
  role: StaffRole,
): { sentence: string; detail: string | null } {
  if (outcome.kind !== "landed" && outcome.error !== undefined) {
    return failureCopy(outcome.error, { role, context: "write" });
  }
  return { sentence: outcomeSentence(outcome, role), detail: null };
}

function showBanner(el: HTMLElement, message: string, ok: boolean, detail?: string): void {
  clear(el);
  el.hidden = false;
  el.className = ok ? "banner banner-ok" : "banner banner-err";
  el.removeAttribute("hidden");
  // Both parts can quote API text (a server message, a request id): clean them.
  el.append(document.createTextNode(amVisibleDisplayText(message)));
  if (detail !== undefined && detail !== "") {
    el.append(disclosure("Show details", h("p", {}, [amVisibleDisplayText(detail)])));
  }
}
